// Iter XX.172 — Focused entry-chip taxonomy.
//
// Each broad chip (`got_out`, `bowling`, `confidence`) reveals a
// vertical stack of situational choices when the FOCUSED experiment
// arm is active. Every focused choice is:
//   · A phrase a cricketer actually says
//   · A situation XX.167's `_L2_SITUATIONAL_MARKERS` or
//     `_L3_SPECIFICITY_MARKERS` already recognises (see
//     `backend/first_reply_frame.py`)
//   · EVIDENCE, not diagnosis — the LLM still has to reason
//
// Message actually sent to /web/chat on selection:
//   "<broad label> — <focused label>."
// This concatenation deterministically upgrades XX.167's evidence
// classification from L1 → L2 (or L3), so the first Gully reply
// leans on the richer-evidence branch that already exists.
//
// "Something else" (id = "other") on every set — routes to the
// composer so free-text stays a first-class path.
//
// `parent_worry` and `video` are intentionally NOT expanded here.
// Video is upload-first; parent expansion is out of XX.172 scope.

export const FOCUSED_CHIPS = {
  got_out: [
    { id: "out_early",         label: "I keep getting out early" },
    { id: "starts_no_go_big",  label: "I make starts but don't go big" },
    { id: "dot_ball_pressure", label: "Dot balls create pressure" },
    { id: "struggle_pace",     label: "I struggle against pace" },
    { id: "struggle_spin",     label: "I struggle against spin" },
    { id: "technique_timing",  label: "My timing/technique feels wrong" },
    { id: "other",             label: "Something else" },
  ],
  bowling: [
    { id: "line_length",         label: "I can't control line and length" },
    { id: "not_taking_wickets",  label: "I'm not taking wickets" },
    { id: "lost_pace",           label: "I've lost pace" },
    { id: "one_bad_over",        label: "One bad over ruins my spell" },
    { id: "action_feels_wrong",  label: "My action feels wrong" },
    { id: "variation_off",       label: "My variation isn't working" },
    { id: "other",               label: "Something else" },
  ],
  confidence: [
    { id: "out_cheaply",           label: "I keep getting out cheaply" },
    { id: "nets_vs_match",         label: "I'm good in nets but struggle in matches" },
    { id: "pressure_changes_play", label: "Pressure changes how I play" },
    { id: "one_mistake",           label: "One mistake has knocked my confidence" },
    { id: "not_selected",          label: "I'm not getting selected" },
    { id: "other",                 label: "Something else" },
  ],
};

/**
 * Return true if the broad chip id has a focused-chip layer.
 * `parent_worry` and `video` return false — see module docstring.
 */
export function hasFocusedLayer(broadChipId) {
  return Object.prototype.hasOwnProperty.call(FOCUSED_CHIPS, broadChipId);
}

/**
 * Compose the natural-language first message from broad + focused
 * labels. Format: "<broad> — <focused>.". Called only for the
 * FOCUSED arm and only when the focused chip is NOT "other".
 */
export function composeFocusedMessage(broadLabel, focusedLabel) {
  const b = String(broadLabel || "").trim();
  const f = String(focusedLabel || "").trim();
  if (!b) return f;
  if (!f) return b;
  const trimmed = f.replace(/[.!?]+$/u, "");
  return `${b} — ${trimmed}.`;
}
